"use client";

import { ActiveNavDecorations } from "@/components/decorations/ActiveNavDecorations";
import { HeaderDailyNavLink } from "@/components/layout/header-daily-nav-link";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { showDailyPuzzleNavLink } from "@/lib/constants/daily-puzzle-nav";
import { Shield, SignOut } from "@phosphor-icons/react";
import type { User } from "@supabase/supabase-js";
import Link from "next/link";
import { usePathname } from "next/navigation";

interface HeaderDesktopNavProps {
  user: User | null;
  isAdmin: boolean;
  isLoading: boolean;
  handleLogout: () => Promise<void>;
  getUserInitials: () => string;
}

export function HeaderDesktopNav({
  user,
  isAdmin,
  isLoading,
  handleLogout,
  getUserInitials,
}: HeaderDesktopNavProps) {
  const pathname = usePathname();

  if (isLoading) {
    return (
      <nav aria-label="Desktop navigation" className="flex items-center gap-4">
        <Skeleton className="h-8 w-8 rounded-full" aria-hidden="true" />
      </nav>
    );
  }

  if (user) {
    return (
      <nav aria-label="Desktop navigation" className="flex items-center gap-8">
        <Link
          href="/about"
          className="text-white hover:text-gray-300 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded px-1"
          aria-current={pathname === "/about" ? "page" : undefined}
        >
          About
        </Link>

        {/* Partners link */}
        <Link
          href="/partners"
          className="text-white hover:text-gray-300 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded px-1"
          aria-current={pathname === "/partners" ? "page" : undefined}
        >
          Partners
        </Link>
        <Link
          href="/educators"
          className="text-white hover:text-gray-300 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded px-1"
          aria-current={pathname === "/educators" ? "page" : undefined}
        >
          Educators
        </Link>
        {showDailyPuzzleNavLink && <HeaderDailyNavLink />}

        {/* See Games button and User dropdown grouped together with smaller gap */}
        <div className="flex items-center gap-3">
          {/* See Games button */}
          <Button
            asChild
            variant="outline"
            className="bg-white hover:bg-gray-100 text-black font-bold rounded-full px-6 border-0 transition-transform hover:scale-105 motion-reduce:transition-none motion-reduce:hover:scale-100"
          >
            <Link href="/games">See Games</Link>
          </Button>

          {/* User dropdown */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-full"
                aria-label="User menu"
                aria-haspopup="menu"
              >
                <Avatar className="h-10 w-10 bg-brand-purple/90 text-white cursor-pointer hover:bg-brand-purple-dark transition-all hover:scale-105 motion-reduce:transition-none motion-reduce:hover:scale-100">
                  <AvatarFallback className="bg-brand-purple text-white font-medium">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem className="flex-col items-start" disabled>
                <span className="text-xs text-gray-500">Signed in as:</span>
                <span className="font-medium truncate">{user?.email}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {isAdmin && (
                <DropdownMenuItem asChild>
                  <Link
                    href="/admin"
                    className="cursor-pointer flex items-center"
                    title="Admin Dashboard"
                  >
                    <Shield
                      weight="fill"
                      className="mr-2 h-4 w-4"
                      strokeWidth={1.5}
                      aria-hidden="true"
                    />
                    <span>Go to admin panel</span>
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                <SignOut weight="fill" className="mr-2 h-4 w-4" aria-hidden="true" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Right nav decoration (desktop and up) */}
        <ActiveNavDecorations placement="right" />
      </nav>
    );
  }

  // Show About link, Partners link, and Play Games button for unauthenticated users
  return (
    <nav aria-label="Desktop navigation" className="flex items-center gap-8">
      <Link
        href="/about"
        className="text-white hover:text-gray-300 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded px-1"
        aria-current={pathname === "/about" ? "page" : undefined}
      >
        About
      </Link>
      <Link
        href="/partners"
        className="text-white hover:text-gray-300 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded px-1"
        aria-current={pathname === "/partners" ? "page" : undefined}
      >
        Partners
      </Link>

      <Link
        href="/educators"
        className="text-white hover:text-gray-300 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded px-1"
        aria-current={pathname === "/educators" ? "page" : undefined}
      >
        Educators
      </Link>
      {showDailyPuzzleNavLink && <HeaderDailyNavLink />}
      <Button
        asChild
        variant="outline"
        className="bg-white hover:bg-gray-100 text-black font-bold rounded-full px-6 border-0 transition-transform hover:scale-105 motion-reduce:transition-none motion-reduce:hover:scale-100"
      >
        <Link href="/games">See Games</Link>
      </Button>

      {/* Right nav decoration (desktop and up) */}
      <ActiveNavDecorations placement="right" />
    </nav>
  );
}
